import * as React from "react";
import { PFTResults, Parameters, TestRepeatabilityResults } from './PFTAPITypes';

export interface AcceptabilityState {
    spirometryResults: PFTResults,
    loadingBestValues: boolean,
    loadingAveragesValues: boolean,
    changedAcceptability: boolean
}

const initialState: AcceptabilityState = {
    spirometryResults: {
        AnalysisSetId: null,
        BestParameters: [],
        DisplayParameters: [],
        DisplayRepeatabilityParameters: [],
        SessionId: null,
        SoftwareInterpretation: null,
        TestResults: [],
        ParametersAverages: []
    },
    loadingBestValues: false,
    loadingAveragesValues: false,
    changedAcceptability: true
}

export enum AcceptabilityActionTypes {
    LoadInitialAcceptability =  "LoadInitialAcceptability",
    ToggleTestAcceptability = "ToggleTestAcceptability",
    LoadNewTest = "LoadNewTest",
    UpdateBestDlcoTest = "UpdateDlcoBestTest",
    LoadingBestValues = "LoadingBestValues",
    LoadingAveragesValues = "LoadingAveragesValues",
    UpdateRepeatability = "UpdateRepeatability",
    DoneLoadingBestValues = "DoneLoadingBestValues",
    DoneLoadingAveragesValues = "DoneLoadingAveragesValues",
    LoadingReversibilityChanges = "LoadingReversibilityChanges",
    DoneLoadingReversibilityChanges = "DoneLoadingReversibilityChanges",
    UpdateBestTest = "UpdateBestTest",
    UpdateAverages = "UpdateAverages",
}

export type AcceptabilityActions = LoadInitialAcceptabilityAction | ToggleTestAcceptabilityAction | UpdateBestTestAction | LoadingBestValuesAction | UpdateRepeatabilityAction | DoneLoadingBestValuesAction | DoneLoadingReversibilityChangesAction | LoadingReversibilityChangesAction | UpdateAveragesAction | LoadingAveragesValuesAction  | DoneLoadingAveragesValuesAction;

export class LoadInitialAcceptabilityAction {
    type: AcceptabilityActionTypes.LoadInitialAcceptability;
    results: PFTResults;

    static create(results: PFTResults): LoadInitialAcceptabilityAction {
        return {
            type: AcceptabilityActionTypes.LoadInitialAcceptability,
            results: results,
        };
    }
}

export class ToggleTestAcceptabilityAction {
    type: AcceptabilityActionTypes.ToggleTestAcceptability;
    testId: string;
    checked: boolean;

    static create(testId: string, checked: boolean): ToggleTestAcceptabilityAction {
        return {
            type: AcceptabilityActionTypes.ToggleTestAcceptability,
            testId: testId,
            checked: checked,
        };
    }
}

export class UpdateBestTestAction {
    type: AcceptabilityActionTypes.UpdateBestTest;
    bestTests: Parameters;

    static create(bestTests: Parameters): UpdateBestTestAction {
        return {
            type: AcceptabilityActionTypes.UpdateBestTest,
            bestTests: bestTests
        };
    }
}

export class UpdateAveragesAction {
    type: AcceptabilityActionTypes.UpdateAverages;
    averages: Parameters;

    static create(averages: Parameters): UpdateAveragesAction {
        return {
            type: AcceptabilityActionTypes.UpdateAverages,
            averages: averages
        };
    }
}
export class UpdateRepeatabilityAction {
    type: AcceptabilityActionTypes.UpdateRepeatability;
    repeatability: TestRepeatabilityResults[];

    static create(repeatability: TestRepeatabilityResults[]): UpdateRepeatabilityAction {
        return {
            type: AcceptabilityActionTypes.UpdateRepeatability,
            repeatability: repeatability
        };
    }
}

export class LoadingBestValuesAction {
    type: AcceptabilityActionTypes.LoadingBestValues

    static create(): LoadingBestValuesAction {
        return {
            type: AcceptabilityActionTypes.LoadingBestValues
        };
    }
}

export class DoneLoadingBestValuesAction {
    type: AcceptabilityActionTypes.DoneLoadingBestValues

    static create(): DoneLoadingBestValuesAction {
        return {
            type: AcceptabilityActionTypes.DoneLoadingBestValues
        };
    }
}

export class LoadingAveragesValuesAction {
    type: AcceptabilityActionTypes.LoadingAveragesValues

    static create(): LoadingAveragesValuesAction {
        return {
            type: AcceptabilityActionTypes.LoadingAveragesValues
        };
    }
}



export class DoneLoadingAveragesValuesAction {
    type: AcceptabilityActionTypes.DoneLoadingAveragesValues

    static create(): DoneLoadingAveragesValuesAction {
        return {
            type: AcceptabilityActionTypes.DoneLoadingAveragesValues
        };
    }
}

export class LoadingReversibilityChangesAction {
    type: AcceptabilityActionTypes.LoadingReversibilityChanges

    static create(): LoadingReversibilityChangesAction {
        return {
            type: AcceptabilityActionTypes.LoadingReversibilityChanges
        };
    }
}

export class DoneLoadingReversibilityChangesAction {
    type: AcceptabilityActionTypes.DoneLoadingReversibilityChanges

    static create(): DoneLoadingReversibilityChangesAction {
        return {
            type: AcceptabilityActionTypes.DoneLoadingReversibilityChanges
        };
    }
}

export const useAcceptability = (pFTResults: PFTResults): [AcceptabilityState, React.Dispatch<any>] => {
    const reducer = (state: AcceptabilityState, action: AcceptabilityActions) => {
        switch (action.type) {
            case AcceptabilityActionTypes.LoadInitialAcceptability:
                return action.results ? {
                    ...state,
                    spirometryResults: action.results,
                } : initialState;
            case AcceptabilityActionTypes.ToggleTestAcceptability:
                return {
                    ...state,
                    spirometryResults: {
                        ...state.spirometryResults,
                        TestResults: state.spirometryResults.TestResults.map(tr => {
                            if (tr.TestId === action.testId) {
                                return {
                                    ...tr,
                                    OverreaderAcceptability: !tr.OverreaderAcceptability,
                                }
                            } else {
                                return tr;
                            }
                        }),
                    },
                    changedAcceptability: !state.changedAcceptability
                };
            case AcceptabilityActionTypes.UpdateBestTest:
                return {
                    ...state,
                    spirometryResults: {
                        ...state.spirometryResults,
                        BestParameters: action.bestTests
                    }
                }
            case AcceptabilityActionTypes.LoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: true
                }
            case AcceptabilityActionTypes.DoneLoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: false
                }
            
                case AcceptabilityActionTypes.UpdateAverages:
                    return {
                        ...state,
                        spirometryResults: {
                            ...state.spirometryResults,
                            ParametersAverages: action.averages
                        }
                    }
                case AcceptabilityActionTypes.LoadingAveragesValues:
                    return {
                        ...state,
                        loadingAveragesValues: true
                    }
                case AcceptabilityActionTypes.DoneLoadingAveragesValues:
                    return {
                        ...state,
                        loadingAveragesValues: false
                    }
                
            case AcceptabilityActionTypes.LoadingReversibilityChanges:
                return {
                    ...state,
                    LoadingReversibilityChangesAction: true
                }
            case AcceptabilityActionTypes.DoneLoadingReversibilityChanges:
                return {
                    ...state,
                    LoadingReversibilityChangesAction: false
                }
            case AcceptabilityActionTypes.UpdateRepeatability:
                return {
                    ...state,
                    spirometryResults: {
                        ...state.spirometryResults,
                        TestResults: state.spirometryResults.TestResults.map(tr => {
                            const testRepeatabilityResult = action.repeatability.find(trRes => trRes.TestId === tr.TestId);

                            return {
                                ...tr,
                                RepeatabilityParameters: testRepeatabilityResult.RepeatabilityParameters
                            };

                        })
                    }
                }
            default:
                return state;
        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialState);

    React.useEffect(() => {
        const hydrateAcceptability = () => {
            dispatch(LoadInitialAcceptabilityAction.create(pFTResults));
        };

        hydrateAcceptability();
    }, [pFTResults])

    return [state, dispatch];
}
